import { useCallback, useEffect, useMemo, useState } from 'react';
import { useT } from '@repo/common';
import { searchSchools } from '../../../api/pathfinder/pathfinder.api';
import PathfinderDetailSearch from './PathfinderDetailSearch';

const COUNTRY_LABELS = {
  AU: 'Australia',
  CA: 'Canada',
  CN: 'China',
  GB: 'United Kingdom',
  JP: 'Japan',
  KR: 'South Korea',
  NL: 'Netherlands',
  NZ: 'New Zealand',
  QA: 'Qatar',
  US: 'United States',
  VN: 'Vietnam',
};

const SCHOOL_SEARCH_COUNTRIES = [
  'Accredited Special School',
  'Albania',
  'Algeria',
  'Argentina',
  'Austria',
  'Bahrain',
  'Belarus',
  'Belgium',
  'Botswana',
  'Brazil',
  'Brunei',
  'Burkina Faso',
  'Burundi',
  'Cameroon',
  'Canada',
  'Chile',
  'China',
  'Colombia',
  "Cote d'Ivoire",
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Democratic Republic of the Congo',
  'Dominican Republic',
  'Egypt',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Fiji',
  'Finland',
  'Foreign',
  'France',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Hungary',
  'India',
  'Iran',
  'Italy',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kuwait',
  'Kyrgyzstan',
  'Latvia',
  'Lesotho',
  'Luxembourg',
  'Madagascar',
  'Malta',
  'Mexico',
  'Moldova',
  'Mongolia',
  'Morocco',
  'Netherlands',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Norway',
  'Pakistan',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Poland',
  'Portugal',
  'Romania',
  'Russia',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'South Korea',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Sweden',
  'Switzerland',
  'Taiwan',
  'Thailand',
  'Togo',
  'Turkmenistan',
  'Turkey',
  'Ukraine',
  'United Kingdom',
  'United States',
  'Uzbekistan',
  'Vanuatu',
  'Vietnam',
].sort((left, right) => right.length - left.length);

// 한국어 국가명 → 영어 매핑 (seed_school_master searchText 첫 토큰 기준)
const KO_TO_EN_COUNTRY = {
  '네덜란드': 'Netherlands',
  '미국': 'United States',
  '영국': 'United Kingdom',
  '호주': 'Australia',
  '캐나다': 'Canada',
  '중국': 'China',
  '일본': 'Japan',
  '한국': 'South Korea',
  '뉴질랜드': 'New Zealand',
  '카타르': 'Qatar',
  '베트남': 'Vietnam',
  '독일': 'Germany',
  '프랑스': 'France',
  '싱가포르': 'Singapore',
  '태국': 'Thailand',
  '인도': 'India',
  '말레이시아': 'Malaysia',
  '필리핀': 'Philippines',
  '인도네시아': 'Indonesia',
  '홍콩': 'Hong Kong',
  '대만': 'Taiwan',
  '스위스': 'Switzerland',
  '스웨덴': 'Sweden',
  '노르웨이': 'Norway',
  '덴마크': 'Denmark',
  '핀란드': 'Finland',
  '오스트리아': 'Austria',
  '벨기에': 'Belgium',
  '스페인': 'Spain',
  '이탈리아': 'Italy',
  '포르투갈': 'Portugal',
  '러시아': 'Russia',
  '브라질': 'Brazil',
  '멕시코': 'Mexico',
  '아르헨티나': 'Argentina',
  '아랍에미리트': 'UAE',
  '사우디아라비아': 'Saudi Arabia',
  '이스라엘': 'Israel',
  '터키': 'Turkey',
  '남아프리카': 'South Africa',
  '이집트': 'Egypt',
};

const formatSchoolTitle = (school) => school.schoolNameEn || school.schoolNameKo || String(school.id);

const normalizeSpace = (value = '') => value.replace(/\s+/g, ' ').trim();

const extractLocationPrefix = (school) => {
  const normalizedText = normalizeSpace(school.searchText);
  const schoolTitle = normalizeSpace(formatSchoolTitle(school));
  if (!normalizedText) {
    return '';
  }

  if (schoolTitle && normalizedText.endsWith(schoolTitle)) {
    return normalizeSpace(
      normalizedText.slice(0, normalizedText.length - schoolTitle.length)
    );
  }

  return '';
};

const cleanLocationDetail = (value = '') => {
  const normalizedValue = normalizeSpace(value);
  const parenthesized = normalizedValue.match(/^\(([^)]+)\)(?:\s+Elementary School)?$/);

  return normalizeSpace(parenthesized?.[1] ?? normalizedValue);
};

const parseLocationFromSearchText = (school) => {
  const prefix = extractLocationPrefix(school);
  if (!prefix) {
    return { countryLabel: '', cityLabel: '' };
  }

  const countryLabel =
    SCHOOL_SEARCH_COUNTRIES.find(
      (country) =>
        prefix === country ||
        prefix.startsWith(`${country} `) ||
        prefix.startsWith(`${country}(`) ||
        prefix.startsWith(`${country}[`)
    ) ?? '';

  if (!countryLabel) {
    return { countryLabel: prefix, cityLabel: '' };
  }

  return {
    countryLabel,
    cityLabel: cleanLocationDetail(prefix.slice(countryLabel.length)),
  };
};

const hasHangulText = (value = '') => /[가-힣]/.test(value);

const resolveCountryLabel = (school) => {
  // 백엔드가 country_code(ISO2)에서 역산한 영문 국가명을 최우선 사용
  const resolvedName = school.countryName?.trim();
  if (resolvedName && !hasHangulText(resolvedName)) {
    return resolvedName;
  }

  const countryCode = school.countryCode?.trim()?.toUpperCase();
  if (countryCode) {
    return COUNTRY_LABELS[countryCode] || countryCode;
  }

  const parsedCountry = parseLocationFromSearchText(school).countryLabel;
  if (parsedCountry) {
    return parsedCountry;
  }

  if (!resolvedName) return '';
  return KO_TO_EN_COUNTRY[resolvedName] || resolvedName;
};

const INSTITUTION_WORDS_RE = /\b(?:school|college|academy|university|institute|kindergarten|lyceum|grammar|primary|secondary|campus|community|center|centre|tutorial)\b/i;

const extractCityFromTitle = (title = '') => {
  const normalizedTitle = normalizeSpace(title);
  if (!normalizedTitle) return '';

  const matches = [...normalizedTitle.matchAll(/\(([^()]+)\)/g)];
  const candidate = matches.length > 0 ? matches[matches.length - 1][1]?.trim() : '';

  if (!candidate) return '';
  if (/\d/.test(candidate)) return '';
  if (/^[A-Z0-9]{2,6}$/.test(candidate)) return '';
  if (INSTITUTION_WORDS_RE.test(candidate)) return '';

  return normalizeSpace(candidate);
};

const resolveCityLabel = (school) => {
  // 백엔드 정규 컬럼(city > region) 우선, 없으면 기존 searchText 파싱 폴백
  const city = school.city?.trim();
  if (city) return city;
  const region = school.region?.trim();
  if (region) return region;

  const fromSearchText = parseLocationFromSearchText(school).cityLabel;
  if (fromSearchText) return fromSearchText;
  return extractCityFromTitle(formatSchoolTitle(school));
};

const formatSchoolSubtitle = (school) => {
  const parts = [school.schoolNameKo, school.cityLabel, school.countryLabel].filter(Boolean);
  return parts.join(' · ');
};

const dedupeSearchParts = (values) =>
  values
    .map((value) => normalizeSpace(String(value || '')))
    .filter(Boolean)
    .filter(
      (value, index, normalizedValues) =>
        normalizedValues.findIndex(
          (candidate) => candidate.toLowerCase() === value.toLowerCase()
        ) === index
    );

const buildSearchKeyword = ({ query = '', cityValue = '', countryValue = '' }) => {
  const normalizedQuery = normalizeSpace(String(query || ''));
  if (normalizedQuery) {
    return normalizedQuery;
  }

  return dedupeSearchParts([countryValue, cityValue]).join(' ');
};

function PathfinderSchoolMasterSearchSelect({
  value = null,
  onChange,
  selectedLabelValue = '',
  placeholder,
}) {
  const t = useT();
  const resolvedPlaceholder =
    placeholder ?? t('Search school', '학교를 검색해 주세요');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');

  const enrichedResults = useMemo(
    () =>
      results.map((school) => ({
        ...school,
        cityLabel: resolveCityLabel(school),
        countryLabel: resolveCountryLabel(school),
      })),
    [results]
  );

  const cityOptions = useMemo(
    () =>
      [
        ...new Set(
          [selectedCity, ...enrichedResults.map((school) => school.cityLabel)].filter(
            Boolean
          )
        ),
      ]
        .sort((left, right) => left.localeCompare(right))
        .map((label) => ({
          value: label,
          label,
        })),
    [enrichedResults, selectedCity]
  );

  const countryOptions = useMemo(
    () =>
      [
        ...new Set(
          [
            selectedCountry,
            ...SCHOOL_SEARCH_COUNTRIES,
            ...enrichedResults.map((school) => school.countryLabel),
          ].filter(Boolean)
        ),
      ]
        .sort((left, right) => left.localeCompare(right))
        .map((label) => ({
          value: label,
          label,
        })),
    [enrichedResults, selectedCountry]
  );

  const filteredResults = useMemo(
    () =>
      enrichedResults.filter((school) => {
        const normalizedSearchText = normalizeSpace(school.searchText || '').toLowerCase();

        if (selectedCity) {
          const normalizedSelectedCity = normalizeSpace(selectedCity).toLowerCase();
          const normalizedCityLabel = normalizeSpace(school.cityLabel || '').toLowerCase();
          const cityMatched =
            normalizedSearchText.includes(normalizedSelectedCity) ||
            (normalizedCityLabel &&
              (normalizedCityLabel.includes(normalizedSelectedCity) ||
                normalizedSelectedCity.includes(normalizedCityLabel)));

          if (!cityMatched) {
            return false;
          }
        }

        if (selectedCountry) {
          const normalizedSelectedCountry = normalizeSpace(selectedCountry).toLowerCase();
          const normalizedCountryLabel = normalizeSpace(school.countryLabel || '').toLowerCase();
          const countryMatched =
            normalizedSearchText.includes(normalizedSelectedCountry) ||
            (normalizedCountryLabel &&
              (normalizedCountryLabel.includes(normalizedSelectedCountry) ||
                normalizedSelectedCountry.includes(normalizedCountryLabel)));

          if (!countryMatched) {
            return false;
          }
        }

        return true;
      }),
    [enrichedResults, selectedCity, selectedCountry]
  );

  const mappedResults = useMemo(() => {
    const nextResults = filteredResults.map((school) => ({
      value: String(school.id),
      title: formatSchoolTitle(school),
      subtitle: formatSchoolSubtitle(school),
    }));
    const normalizedSelectedValue = value ? String(value) : '';

    if (
      selectedResult &&
      !nextResults.some((result) => result.value === selectedResult.value)
    ) {
      return [selectedResult, ...nextResults];
    }

    if (
      normalizedSelectedValue &&
      selectedLabelValue &&
      !nextResults.some((result) => result.value === normalizedSelectedValue)
    ) {
      return [
        {
          value: normalizedSelectedValue,
          title: selectedLabelValue,
          subtitle: '',
        },
        ...nextResults,
      ];
    }

    return nextResults;
  }, [filteredResults, selectedLabelValue, selectedResult, value]);

  const handleSearch = useCallback(
    async ({
      query: nextQuery,
      cityValue: nextCityValue,
      countryValue: nextCountryValue,
    }) => {
      const keyword = buildSearchKeyword({
        query: nextQuery,
        cityValue: nextCityValue,
        countryValue: nextCountryValue,
      });
      setHasSearched(true);

      if (!keyword) {
        setResults([]);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await searchSchools(keyword);
        setResults(Array.isArray(response) ? response : []);
      } catch {
        setResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (
      selectedCity &&
      cityOptions.length > 0 &&
      !cityOptions.some((option) => option.value === selectedCity)
    ) {
      setSelectedCity('');
    }
  }, [cityOptions, selectedCity]);

  useEffect(() => {
    if (
      selectedCountry &&
      countryOptions.length > 0 &&
      !countryOptions.some((option) => option.value === selectedCountry)
    ) {
      setSelectedCountry('');
    }
  }, [countryOptions, selectedCountry]);

  const handleSelectedChange = useCallback((nextValue, result) => {
    const normalizedValue = nextValue ? String(nextValue) : null;
    onChange(normalizedValue, {
      selectedOptions: result
        ? [{ value: normalizedValue, label: result.title }]
        : [],
    });
    setSelectedResult(
      result
        ? {
            value: normalizedValue,
            title: result.title,
            subtitle: result.subtitle,
          }
        : null
    );
  }, [onChange]);

  return (
    <PathfinderDetailSearch
      placeholder={resolvedPlaceholder}
      query={query}
      onQueryChange={setQuery}
      cityOptions={cityOptions}
      countryOptions={countryOptions}
      cityValue={selectedCity}
      countryValue={selectedCountry}
      results={mappedResults}
      resultsLoading={searchLoading}
      searchLoading={searchLoading}
      hasSearched={hasSearched}
      selectedValue={value ? String(value) : ''}
      defaultSelectedValue=''
      onSelectedChange={handleSelectedChange}
      onSearch={handleSearch}
      searchButtonText={t('Search', '검색')}
      selectedLabel={t('Selected :', '선택한 항목 :')}
      emptySelectedText={t('No added yet', '아직 추가한 항목이 없어요')}
      resultsLabel={t('Results :', '검색 결과 :')}
      useQueryLabel={(nextQuery) =>
        t(
          `Use "${nextQuery}" as your school`,
          `"${nextQuery}"을(를) 내 학교로 사용하기`,
        )
      }
      defaultCityValue=''
      defaultCountryValue=''
      onCityChange={(nextValue) => {
        setSelectedCity(nextValue);
        if (nextValue) {
          handleSearch({ query, cityValue: nextValue, countryValue: selectedCountry });
        }
      }}
      onCountryChange={(nextValue) => {
        setSelectedCountry(nextValue);
        if (nextValue) {
          handleSearch({ query, cityValue: selectedCity, countryValue: nextValue });
        }
      }}
      cityOptionsLoading={false}
      countryOptionsLoading={false}
      hasMoreResults={false}
      moreResultsLoading={false}
      onLoadMore={undefined}
      title={null}
      progressLabel={null}
      className='w-full'
    />
  );
}

export default PathfinderSchoolMasterSearchSelect;
